/**
 * 操作ログフィルターサイドバー
 */

import {
  Paper,
  Stack,
  Group,
  Text,
  MultiSelect,
  Divider,
  Button,
  ScrollArea,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import "@mantine/dates/styles.css";
import { Filter, X } from "lucide-react";
import { ACTION_OPTIONS, RESOURCE_OPTIONS } from "../../constants/operationLogOptions";

export interface OperationLogFilters {
  actionFilter: string[];
  resourceFilter: string[];
  startDate: Date | null;
  endDate: Date | null;
}

interface OperationLogFilterSidebarProps {
  filters: OperationLogFilters;
  onChange: (filters: OperationLogFilters) => void;
  onReset: () => void;
}

/**
 * 操作ログフィルターサイドバー
 */
export function OperationLogFilterSidebar({
  filters,
  onChange,
  onReset,
}: OperationLogFilterSidebarProps) {
  const { actionFilter, resourceFilter, startDate, endDate } = filters;
  const hasFilters =
    actionFilter.length > 0 || resourceFilter.length > 0 || !!startDate || !!endDate;

  const updateFilter = <K extends keyof OperationLogFilters>(
    key: K,
    value: OperationLogFilters[K]
  ) => {
    onChange({ ...filters, [key]: value });
  };

  return (
    <Paper
      shadow="xs"
      p="md"
      radius="md"
      withBorder
      style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <ScrollArea flex={1} style={{ margin: "-12px", padding: "12px" }}>
        <Stack gap="md">
          <Group gap="xs">
            <Filter size={18} />
            <Text fw={700} size="sm">
              フィルター
            </Text>
          </Group>

          <MultiSelect
            label="アクション"
            placeholder="種別を選択"
            data={ACTION_OPTIONS}
            value={actionFilter}
            onChange={(val) => updateFilter("actionFilter", val)}
            clearable
            searchable
            nothingFoundMessage="なし"
            size="sm"
          />

          <MultiSelect
            label="対象"
            placeholder="対象を選択"
            data={RESOURCE_OPTIONS}
            value={resourceFilter}
            onChange={(val) => updateFilter("resourceFilter", val)}
            clearable
            searchable
            nothingFoundMessage="なし"
            size="sm"
          />

          <Divider label="期間" labelPosition="center" />

          <DateTimePicker
            label="開始"
            placeholder="開始日時"
            value={startDate}
            onChange={(val) => updateFilter("startDate", val)}
            locale="ja"
            clearable
            size="sm"
          />
          <DateTimePicker
            label="終了"
            placeholder="終了日時"
            value={endDate}
            onChange={(val) => updateFilter("endDate", val)}
            locale="ja"
            clearable
            size="sm"
          />
        </Stack>
      </ScrollArea>

      <Divider mt="md" mb="sm" />
      <Button
        variant={hasFilters ? "light" : "subtle"}
        color={hasFilters ? "red" : "gray"}
        leftSection={<X size={16} />}
        onClick={onReset}
        disabled={!hasFilters}
        size="sm"
        fullWidth
      >
        フィルター解除
      </Button>
    </Paper>
  );
}
